import { FC } from 'react'
import { When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { paths } from '@/constants/paths'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { TheatricalPortraitRail } from '@/organisms/TheatricalPortraitRail'
import { WatchNowCollection } from '@/organisms/WatchNowCollection'
import { TopPicksForYouCollection } from '@/organisms/WatchNowCollection/TopPicksForYouCollection/TopPicksForYouCollection'
import { WatchlistCollection } from '@/organisms/WatchlistCollection'
import { LargePortraitGrid } from '@/page/CollectionDisplay/LargePortraitGrid'
import { ComingSoonCollection } from '@/page/ComingSoonCollection'
import { GuildUpdatesPortraitRowCollection } from '@/page/GuildUpdatesPortraitRowCollection'
import { LivestreamLandscapeRowCollection } from '@/page/LivestreamLandscapeRowCollection'
import { SneakPeekCollection } from '@/page/SneakPeekCollection'
import { useGuildMember } from '@/services/GuildUserService'
import { LinkViewModel, PageDataContext } from '@/services/RenderService/types'
import { useUser } from '@/services/UserService'
import { Collection as CollectionModel } from '@/types/codegen-contentful'
import { ContentMovie, ContentSeries } from '@/types/codegen-federation'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { isDefined } from '@/utils/primitives/generics'
import { TranslateFunction, useTranslate } from '@/utils/translate/translate-client'

/** The collection ID used by the Theatrical Releases Rail */
const THEATRICAL_RELEASES_COLLECTION_ID = '5c7ee724-4deb-4588-9850-e7fe305501cf'

interface Props {
  collection: CollectionModel
  pageDataContext: PageDataContext
  containerPosition?: number
}

const renderPortraitGridCollection = (collection: CollectionModel, pageDataContext: PageDataContext) => {
  if (pageDataContext['all-movies'] || pageDataContext['all-shows']) {
    const contentData = pageDataContext['all-movies'] || pageDataContext['all-shows']
    const mappedLinks = mapContentToLinks(contentData)

    return <LargePortraitGrid links={mappedLinks} id={collection?.title ?? ''} />
  }
}

const renderLandscapeRowCollection = (
  collection: CollectionModel,
  pageDataContext: PageDataContext,
  isGuildMember: boolean,
  isLoggedIn: boolean,
  t: TranslateFunction,
  pathname: string,
  containerPosition: number,
) => {
  switch (collection.dataSource) {
    case 'start-watching':
      return (
        <>
          <LazyHydrate whenVisible>
            <>
              <When condition={isLoggedIn}>
                <PaddedContainer className="mb-12 overflow-x-hidden" fluid>
                  <WatchlistCollection containerPosition={containerPosition} />
                </PaddedContainer>
                <PaddedContainer className="mb-12 overflow-x-hidden" fluid>
                  <TopPicksForYouCollection containerPosition={containerPosition} />
                </PaddedContainer>
              </When>
              <WatchNowCollection
                contentLinks={pageDataContext['start-watching'] ?? []}
                title={collection.title ?? ''}
                onlyShowContinue
                containerPosition={containerPosition}
              />
            </>
          </LazyHydrate>
        </>
      )
    case 'continue-watching':
      return (
        <WatchNowCollection
          contentLinks={pageDataContext['start-watching'] ?? []}
          title={collection.title ?? ''}
          containerPosition={containerPosition}
        />
      )
    case 'early-access':
      return (
        <>
          <When condition={!isGuildMember}>
            <PaddedContainer className="overflow-x-hidden" fluid>
              <SneakPeekCollection
                title={
                  pathname.includes(paths.watch.free)
                    ? t('sneakPeeks', 'Sneak Peeks')
                    : t('startWatchingForFree', 'Start Watching for Free')
                }
                sneakPeekTitleCollection={pageDataContext['sneak-peeks']}
                testId="sneak-peek-collection"
                containerPosition={containerPosition}
              />
            </PaddedContainer>
          </When>
        </>
      )
    case 'livestreams':
      return (
        <PaddedContainer className="overflow-x-hidden" fluid>
          <LivestreamLandscapeRowCollection testId="livestreams-collection" title={collection.title ?? ''} />
        </PaddedContainer>
      )
    default:
      return null
  }
}

const renderPortraitRowCollection = (
  collection: CollectionModel,
  pageDataContext: PageDataContext,
  containerPosition: number,
  t: TranslateFunction,
) => {
  switch (collection.dataSource) {
    case 'guild-updates':
      return (
        <PaddedContainer className="overflow-x-hidden" fluid>
          <GuildUpdatesPortraitRowCollection testId="livestreams-collection" title={collection.title ?? ''} />
        </PaddedContainer>
      )
    case 'coming-soon':
      return (
        <LazyHydrate whenVisible>
          <PaddedContainer className="overflow-x-hidden" fluid>
            <ComingSoonCollection
              ComingSoonTabCollection={pageDataContext['coming-soon-tab'] ?? []}
              testId="coming-soon-collection"
              containerPosition={containerPosition}
            />
          </PaddedContainer>
        </LazyHydrate>
      )
    case 'theatrical':
      return (
        <GBFadeInExperiment experimentName="test-big-screen-top-10-movies" defaultValue={false}>
          <ExperimentVariation variation={false}>{null}</ExperimentVariation>
          <ExperimentVariation variation={true}>
            <PaddedContainer className="overflow-x-hidden" fluid>
              <TheatricalPortraitRail
                title={collection.title ?? t('fromTheBigScreen', 'From the Big Screen')}
                collectionId={THEATRICAL_RELEASES_COLLECTION_ID}
                initialFilms={pageDataContext['theatrical']?.[THEATRICAL_RELEASES_COLLECTION_ID]}
              />
            </PaddedContainer>
          </ExperimentVariation>
        </GBFadeInExperiment>
      )
    default:
      return null
  }
}

export const Collection: FC<Props> = ({ collection, pageDataContext, containerPosition = 0 }) => {
  const { isGuildMember } = useGuildMember()
  const { isLoggedIn } = useUser()
  const { t } = useTranslate('watch')
  const { pathname } = useSafeRouter()

  if (!collection?.dataSource) return null

  switch (collection.displayAs) {
    case 'portrait-grid':
      return renderPortraitGridCollection(collection, pageDataContext)
    case 'landscape-row':
      return renderLandscapeRowCollection(
        collection,
        pageDataContext,
        isGuildMember,
        isLoggedIn,
        t,
        pathname,
        containerPosition,
      )
    case 'portrait-row':
      return renderPortraitRowCollection(collection, pageDataContext, containerPosition, t)
    default:
      return null
  }
}

export const mapContentToLinks = (
  contentData: ContentMovie[] | ContentSeries[] | null | undefined,
): LinkViewModel[] => {
  return (contentData ?? [])
    .map((item) => {
      if (!item?.project?.slug || !item.image?.cloudinaryPath) return null

      return {
        slug: item.project.slug,
        title: item.project.title ?? '',
        imageUrl: item.image.cloudinaryPath,
        linkUrl: `${paths.watch.index}/${item.project.slug}`,
      }
    })
    .filter(isDefined)
}
